import React, { useEffect, useState, createContext, useContext } from 'react';
import db from '../services/database';
import { User } from '../types/database';
interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (userData: Omit<User, 'User_ID'>) => Promise<boolean>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // HARDCODED ADMIN CREDENTIALS
  const ADMIN_EMAIL = 'admin@account.com';
  const ADMIN_PASSWORD = 'admin1234';
  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser) as User;
          // Verify user exists in database
          const dbUser = await db.users.where('Email').equals(userData.Email).first();
          if (dbUser) {
            setUser(dbUser);
            setIsLoggedIn(true);
          } else {
            // User not found in database, clear local storage
            localStorage.removeItem('currentUser');
          }
        } catch (error) {
          console.error('Error checking authentication:', error);
          localStorage.removeItem('currentUser');
        }
      }
    };
    checkAuth();
  }, []);
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('Attempting login with:', email);
      // Special handling for admin login
      if (email === ADMIN_EMAIL) {
        if (password === ADMIN_PASSWORD) {
          // Get admin user from database
          const adminUser = await db.users.where('Email').equals(ADMIN_EMAIL).first();
          if (adminUser) {
            console.log('Admin login successful');
            setUser(adminUser);
            setIsLoggedIn(true);
            localStorage.setItem('currentUser', JSON.stringify(adminUser));
            return true;
          }
        }
        console.log('Admin login failed: Invalid password');
        return false;
      }
      // Regular user login (customers and delivery staff)
      const dbUser = await db.users.where('Email').equals(email).first();
      // Prevent non-admin users from accessing admin accounts
      if (dbUser && dbUser.User_type === 'admin' && email !== ADMIN_EMAIL) {
        console.log('Login failed: Unauthorized admin access attempt');
        return false;
      }
      if (dbUser && dbUser.Password === password && dbUser.User_type !== 'admin') {
        console.log('Login successful, user:', dbUser);
        setUser(dbUser);
        setIsLoggedIn(true);
        localStorage.setItem('currentUser', JSON.stringify(dbUser));
        return true;
      }
      console.log('Login failed: Invalid credentials or user not found');
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };
  const logout = () => {
    setUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem('currentUser');
  };
  const register = async (userData: Omit<User, 'User_ID'>): Promise<boolean> => {
    try {
      // Prevent registration with admin email
      if (userData.Email === ADMIN_EMAIL) {
        console.log('Registration failed: Cannot register with admin email');
        return false;
      }
      // Force user_type to be customer for all registrations
      if (userData.User_type !== 'customer') {
        userData.User_type = 'customer';
      }
      // Check if email already exists
      const existingUser = await db.users.where('Email').equals(userData.Email).first();
      if (existingUser) {
        return false;
      }
      // In a real app, you would hash the password before storing
      const userId = await db.users.add(userData);
      if (userId) {
        const newUser = await db.users.get(userId);
        if (newUser) {
          setUser(newUser);
          setIsLoggedIn(true);
          localStorage.setItem('currentUser', JSON.stringify(newUser));
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  };
  return <AuthContext.Provider value={{
    user,
    isLoggedIn,
    login,
    logout,
    register
  }} data-id="element-203">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};